import React, { useMemo, useState } from 'react';
import { useFinanceDashboard, FinanceDashboardData } from '../../hooks/useFinanceDashboard.js';
import { useFinanceFiltersStore } from '../../store/financeFiltersStore.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { Button } from '../../components/ui/Button.js';
import {
  ComposedChart,
  PieChart,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Line,
  Bar,
  Cell,
  Pie,
} from 'recharts';
import { format, parse, startOfMonth, endOfMonth, subMonths, startOfYear } from 'date-fns';
import { TrendingUp, DollarSign, Users, AlertCircle } from 'lucide-react';

const COLORS = {
  revenue: 'var(--chart-violet)',
  costs: 'var(--chart-indigo)',
  margin: 'var(--chart-blue)',
  deposit: 'var(--chart-violet)',
  balance: 'var(--chart-indigo)',
  pending: 'var(--chart-azure)',
  primary: 'var(--color-primary)',
  success: 'var(--color-success)',
  error: 'var(--color-error)',
  warning: 'var(--color-warning)',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
}

// ─── TimeRangeFilter Component ───
function TimeRangeFilter({
  from,
  to,
  onApply,
}: {
  from: string;
  to: string;
  onApply: (from: string, to: string) => void;
}) {
  const [localFrom, setLocalFrom] = useState(from);
  const [localTo, setLocalTo] = useState(to);
  const now = new Date();

  const applyShortcut = (shortcut: string) => {
    let newFrom = localFrom;
    let newTo = localTo;

    switch (shortcut) {
      case 'thisMonth':
        newFrom = format(startOfMonth(now), 'yyyy-MM-dd');
        newTo = format(endOfMonth(now), 'yyyy-MM-dd');
        break;
      case 'last30':
        newFrom = format(subMonths(now, 1), 'yyyy-MM-dd');
        newTo = format(now, 'yyyy-MM-dd');
        break;
      case 'ytd':
        newFrom = format(startOfYear(now), 'yyyy-MM-dd');
        newTo = format(now, 'yyyy-MM-dd');
        break;
      case 'prevYear':
        newFrom = format(startOfYear(subMonths(now, 12)), 'yyyy-MM-dd');
        newTo = format(subMonths(now, 12), 'yyyy-MM-dd');
        break;
    }

    setLocalFrom(newFrom);
    setLocalTo(newTo);
    onApply(newFrom, newTo);
  };

  return (
    <div
      className="animate-in"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        padding: '1.25rem',
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
      }}
    >
      <div>
        <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
          Periodo
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="date"
            value={localFrom}
            onChange={(e) => setLocalFrom(e.target.value)}
            style={{
              padding: '0.5rem 0.75rem',
              background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
              color: 'var(--color-text)',
              fontSize: 'var(--text-sm)',
              outline: 'none',
            }}
          />
          <input
            type="date"
            value={localTo}
            onChange={(e) => setLocalTo(e.target.value)}
            style={{
              padding: '0.5rem 0.75rem',
              background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
              color: 'var(--color-text)',
              fontSize: 'var(--text-sm)',
              outline: 'none',
            }}
          />
          <Button size="sm" onClick={() => onApply(localFrom, localTo)}>
            Applica
          </Button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <Button variant="ghost" size="sm" onClick={() => applyShortcut('thisMonth')}>
          Questo mese
        </Button>
        <Button variant="ghost" size="sm" onClick={() => applyShortcut('last30')}>
          Ultimi 30 giorni
        </Button>
        <Button variant="ghost" size="sm" onClick={() => applyShortcut('ytd')}>
          Quest&apos;anno
        </Button>
        <Button variant="ghost" size="sm" onClick={() => applyShortcut('prevYear')}>
          Anno precedente
        </Button>
      </div>
    </div>
  );
}

// ─── KPI Header ───
function KpiHeader({ kpi }: { kpi: FinanceDashboardData['kpi'] }) {
  const cards = [
    {
      title: 'Fatturato lordo',
      value: kpi.revenue,
      icon: <DollarSign size={20} style={{ color: COLORS.success }} />,
    },
    {
      title: 'Spese personale',
      value: kpi.personnelCosts,
      icon: <Users size={20} style={{ color: COLORS.warning }} />,
    },
    {
      title: 'Margine stimato',
      value: kpi.estimatedMargin,
      icon: <TrendingUp size={20} style={{ color: COLORS.primary }} />,
    },
    {
      title: 'Incassi in attesa',
      value: kpi.pendingPayments,
      icon: <AlertCircle size={20} style={{ color: COLORS.error }} />,
    },
  ];

  return (
    <div
      className="stagger-children"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '1rem',
      }}
    >
      {cards.map((card, i) => (
        <div
          key={i}
          className="hover-lift"
          style={{
            background: 'var(--glass-bg-card)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)',
            padding: '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            cursor: 'default',
            transition: 'border-color var(--transition-interactive), box-shadow var(--transition-interactive), transform var(--duration-fast) var(--ease-out)',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-primary)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(255,255,255,0.10)'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {card.icon}
            <p style={{
              fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)',
              fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              {card.title}
            </p>
          </div>
          <p style={{ fontSize: 'var(--text-xl)', fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
            {formatCurrency(card.value)}
          </p>
        </div>
      ))}
    </div>
  );
}

// ─── Monthly Trend Chart ───
function MonthlyTrendChart({ data }: { data: FinanceDashboardData['monthlyTrend'] }) {
  return (
    <div
      className="animate-in"
      style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        padding: '1.25rem',
      }}
    >
      <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '1rem' }}>
        Andamento mensile
      </h3>
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
          <XAxis dataKey="month" stroke="var(--color-text-muted)" />
          <YAxis stroke="var(--color-text-muted)" />
          <Tooltip
            contentStyle={{
              background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
              color: 'var(--color-text)',
              fontSize: '12px',
            }}
            formatter={(value: number) => formatCurrency(value)}
          />
          <Legend />
          <Bar dataKey="revenue" fill={COLORS.revenue} name="Ricavi" radius={[4, 4, 0, 0]} />
          <Bar dataKey="costs" fill={COLORS.costs} name="Spese" radius={[4, 4, 0, 0]} />
          <Line
            type="monotone"
            dataKey="cumulativeMargin"
            stroke={COLORS.margin}
            name="Margine YTD"
            strokeWidth={2}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Expense Breakdown Chart ───
function ExpenseBreakdownChart({ data }: { data: FinanceDashboardData['expenseBreakdown'] }) {
  const CHART_COLORS = ['var(--chart-violet)', 'var(--chart-indigo)', 'var(--chart-blue)', 'var(--chart-azure)'];
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div
      className="animate-in"
      style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        padding: '1.25rem',
      }}
    >
      <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '1rem' }}>
        Distribuzione spese
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={2}
            dataKey="value"
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value: number) => formatCurrency(value)} />
        </PieChart>
      </ResponsiveContainer>
      <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {data.map((item, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)' }}>
            <span>{item.name}</span>
            <strong>{formatCurrency(item.value)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Top Collaborators Table ───
function TopCollaboratorsTable({
  data,
  search,
  onSearchChange,
}: {
  data: FinanceDashboardData['topCollaborators'];
  search: string;
  onSearchChange: (s: string) => void;
}) {
  const filtered = useMemo(
    () =>
      data.filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase())
      ),
    [data, search]
  );

  return (
    <div
      className="animate-in"
      style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        padding: '1.25rem',
      }}
    >
      <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '1rem' }}>
        Top Collaboratori
      </h3>
      <input
        type="text"
        placeholder="Ricerca per nome..."
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          marginBottom: '1rem',
          background: 'var(--glass-bg-card)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-card)',
          color: 'var(--color-text)',
          fontSize: 'var(--text-sm)',
          outline: 'none',
          transition: 'border-color var(--transition-interactive)',
        }}
        onFocus={e => { e.currentTarget.style.borderColor = 'var(--color-primary)'; }}
        onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'; }}
      />
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 'var(--text-sm)', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: 600 }}>Nome</th>
              <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: 600 }}>N° eventi</th>
              <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: 600 }}>Totale</th>
              <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: 600 }}>Media</th>
              <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: 600 }}>Min/Max</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? (
              filtered.map((collab) => (
                <tr
                  key={collab.id}
                  style={{
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    transition: 'background var(--transition-interactive)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--glass-bg-card)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ padding: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {collab.avatarUrl && (
                      <img
                        src={collab.avatarUrl}
                        alt={collab.name}
                        style={{ width: 24, height: 24, borderRadius: '50%' }}
                      />
                    )}
                    {collab.name}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>{collab.eventCount}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    <strong>{formatCurrency(collab.totalFee)}</strong>
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    {formatCurrency(collab.avgFee)}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    {formatCurrency(collab.minFee)} / {formatCurrency(collab.maxFee)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                  Nessun risultato
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Top Photographers Table ───
function TopPhotographersTable({
  data,
  search,
  onSearchChange,
}: {
  data: FinanceDashboardData['topExternalPhotographers'];
  search: string;
  onSearchChange: (s: string) => void;
}) {
  const filtered = useMemo(
    () =>
      data.filter((p) =>
        p.name.toLowerCase().includes(search.toLowerCase())
      ),
    [data, search]
  );

  return (
    <div
      className="animate-in"
      style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        padding: '1.25rem',
      }}
    >
      <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '1rem' }}>
        Top Esterni
      </h3>
      <input
        type="text"
        placeholder="Ricerca per nome..."
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          marginBottom: '1rem',
          background: 'var(--glass-bg-card)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-card)',
          color: 'var(--color-text)',
          fontSize: 'var(--text-sm)',
          outline: 'none',
          transition: 'border-color var(--transition-interactive)',
        }}
        onFocus={e => { e.currentTarget.style.borderColor = 'var(--color-primary)'; }}
        onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'; }}
      />
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 'var(--text-sm)', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: 600 }}>Nome</th>
              <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: 600 }}>N° progetti</th>
              <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: 600 }}>Totale</th>
              <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: 600 }}>Media</th>
              <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: 600 }}>Min/Max</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? (
              filtered.map((photog) => (
                <tr
                  key={photog.id}
                  style={{
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    transition: 'background var(--transition-interactive)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--glass-bg-card)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ padding: '0.75rem' }}>{photog.name}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>{photog.eventCount}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    <strong>{formatCurrency(photog.totalFee)}</strong>
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    {formatCurrency(photog.avgFee)}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    {formatCurrency(photog.minFee)} / {formatCurrency(photog.maxFee)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                  Nessun risultato
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Payment Pipeline Chart ───
function PaymentPipelineChart({ data }: { data: FinanceDashboardData['paymentPipeline'] }) {
  return (
    <div
      className="animate-in"
      style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        padding: '1.25rem',
      }}
    >
      <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '1rem' }}>
        Pipeline incassi
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
          <XAxis dataKey="month" stroke="var(--color-text-muted)" />
          <YAxis stroke="var(--color-text-muted)" />
          <Tooltip
            contentStyle={{
              background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
            }}
            formatter={(value: number) => formatCurrency(value)}
          />
          <Legend />
          <Bar dataKey="depositReceived" fill={COLORS.deposit} name="Acconto ricevuto" stackId="a" />
          <Bar dataKey="balanceReceived" fill={COLORS.balance} name="Saldo ricevuto" stackId="a" />
          <Bar dataKey="pending" fill={COLORS.pending} name="Da incassare" stackId="a" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Year Projection ───
function YearProjection({ data }: { data: FinanceDashboardData['yearProjection'] }) {
  if (!data) return null;

  const ytdPercent = (data.ytdRevenue / data.projectedRevenue) * 100;
  const prevYearPercent = data.prevYearRevenue
    ? (data.prevYearRevenue / data.projectedRevenue) * 100
    : 0;

  return (
    <div
      className="animate-in"
      style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        padding: '1.25rem',
      }}
    >
      <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '1.5rem' }}>
        Proiezione fine anno
      </h3>

      <div style={{ marginBottom: '1.5rem' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '0.5rem',
            fontSize: 'var(--text-sm)',
          }}
        >
          <span>YTD</span>
          <strong>{formatCurrency(data.ytdRevenue)}</strong>
        </div>
        <div
          style={{
            width: '100%',
            height: 24,
            background: 'var(--glass-bg-card)',
            borderRadius: 'var(--radius-interactive)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${Math.min(ytdPercent, 100)}%`,
              background: 'var(--color-success)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: '10px',
              fontWeight: 600,
              transition: 'width var(--duration-slow) var(--ease-spring)',
            }}
          >
            {ytdPercent > 5 && `${ytdPercent.toFixed(0)}%`}
          </div>
        </div>
      </div>

      {data.prevYearRevenue !== null && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '0.5rem',
              fontSize: 'var(--text-sm)',
            }}
          >
            <span>Anno precedente (stesso periodo)</span>
            <strong>{formatCurrency(data.prevYearRevenue)}</strong>
          </div>
          <div
            style={{
              width: '100%',
              height: 24,
              background: 'var(--glass-bg-card)',
              borderRadius: 'var(--radius-interactive)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${Math.min(prevYearPercent, 100)}%`,
                background: 'var(--color-text-muted)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '10px',
                fontWeight: 600,
                transition: 'width var(--duration-slow) var(--ease-spring)',
              }}
            >
              {prevYearPercent > 5 && `${prevYearPercent.toFixed(0)}%`}
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          padding: '0.75rem',
          background: 'var(--glass-bg-card)',
          borderRadius: 'var(--radius-card)',
          fontSize: 'var(--text-sm)',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>Target annuale (proiezione):</span>
        <strong>{formatCurrency(data.projectedRevenue)}</strong>
      </div>
    </div>
  );
}

// ─── Main Component ───
export function FinanceDashboard() {
  const { data, isLoading, error } = useFinanceDashboard();
  const { from, to, setDateRange } = useFinanceFiltersStore();
  const [searchCollab, setSearchCollab] = useState('');
  const [searchPhotog, setSearchPhotog] = useState('');

  if (error) {
    return (
      <EmptyState
        title="Errore nel caricamento"
        description="Non è stato possibile caricare i dati della dashboard finanziaria"
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <TimeRangeFilter from={from} to={to} onApply={setDateRange} />

      {isLoading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={120} />
          ))}
        </div>
      ) : !data || Object.keys(data).length === 0 ? (
        <EmptyState
          title="Nessun dato"
          description="Nessun dato finanziario disponibile per il periodo selezionato"
        />
      ) : (
        <>
          <KpiHeader kpi={data.kpi} />
          <MonthlyTrendChart data={data.monthlyTrend} />
          <ExpenseBreakdownChart data={data.expenseBreakdown} />
          <TopCollaboratorsTable
            data={data.topCollaborators}
            search={searchCollab}
            onSearchChange={setSearchCollab}
          />
          <TopPhotographersTable
            data={data.topExternalPhotographers}
            search={searchPhotog}
            onSearchChange={setSearchPhotog}
          />
          <PaymentPipelineChart data={data.paymentPipeline} />
          {data.yearProjection && <YearProjection data={data.yearProjection} />}
        </>
      )}
    </div>
  );
}
